import React from 'react';
import { Row, Col, Alert } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import MiniCard from '../Common/MiniCard';
import {
  isCallCenterOpenAt,
  hasCallCenter,
} from '../../config/callCenterHours';

// What a tile shows when its read failed: a zero there would read as a fact.
const UNKNOWN = '-';

/**
 * How many leads the agent is holding now, and what became of the ones it
 * took today. `today` counts every conversation started since midnight,
 * whatever it is now; `live` is the list under it.
 *
 * The two outcome tiles say "of these" on purpose: they count the outcome of
 * the conversations started today, not the outcomes reached today. One
 * started at 23:30 and handed back at 00:15 is in yesterday's count, and
 * counting by outcome time would take two more listeners and two indexes.
 *
 * A failed read says so above the table instead of showing zeros. The
 * technical detail — for the counts of today, typically Firestore asking for
 * the (companyId, startedAt) index, with the link that creates it — only to
 * whoever can act on it.
 */
const MonitorSummary = ({
  live,
  today,
  company,
  now,
  loading,
  liveError = '',
  todayError = '',
  showDetail = false,
}) => {
  const { t } = useTranslation();

  const callCenter = !hasCallCenter(company)
    ? { text: t('No hours configured'), color: 'secondary' }
    : isCallCenterOpenAt(company, now)
      ? { text: t('Open right now'), color: 'success' }
      : { text: t('Closed right now'), color: 'dark' };

  const completed = today.filter(({ status }) => status === 'completed');
  const returned = today.filter(({ status }) => status === 'abandoned');
  const byHand = returned.filter(
    ({ handoffReason }) => handoffReason === 'manual',
  );

  const todayCount = (count) => (todayError ? UNKNOWN : count);

  const metrics = [
    {
      title: t('Call center'),
      text: callCenter.text,
      color: callCenter.color,
      icon: 'bx bx-headphone',
    },
    {
      title: t('Held by the agent now'),
      text: liveError ? UNKNOWN : live.length,
      color: 'info',
      icon: 'bx bx-bot',
      loading,
    },
    {
      title: t('Started today'),
      text: todayCount(today.length),
      color: 'secondary',
      icon: 'bx bx-message-dots',
    },
    {
      title: t('Of these, closed by the agent'),
      text: todayCount(completed.length),
      color: 'success',
      icon: 'bx bx-check-circle',
    },
    {
      title: t('Of these, back to the call center'),
      text: todayCount(returned.length),
      color: 'warning',
      icon: 'bx bx-phone-incoming',
      percentage:
        !todayError && byHand.length
          ? `${byHand.length} ${t('by hand')}`
          : undefined,
    },
  ];

  return (
    <>
      <Row>
        {metrics.map((metric) => (
          <Col key={metric.title} md={6} xl>
            <MiniCard {...metric} />
          </Col>
        ))}
      </Row>
      {liveError && (
        <Alert color="danger">
          {t(
            'The conversations in progress could not be loaded: the table below may be empty even if the agent is holding some. Reload the page, and if it lasts tell an administrator.',
          )}
          {showDetail && (
            <div className="font-size-11 mt-1 text-break">{liveError}</div>
          )}
        </Alert>
      )}
      {todayError && (
        <Alert color="warning">
          {t(
            'The counts of today could not be loaded. Right after a release the database may still be preparing them: try again in a few minutes, and if it lasts tell an administrator.',
          )}
          {showDetail && (
            <div className="font-size-11 mt-1 text-break">{todayError}</div>
          )}
        </Alert>
      )}
    </>
  );
};

export default MonitorSummary;
